import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { LOGO_URL } from "../lib/constants";
import { GoogleAuthButton } from "../components/GoogleAuthButton";
import { useAuth } from "../lib/auth";
import { formatApiError } from "../lib/api";
import { ShieldCheck, Sparkles, Lock, Mail, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
    const location = useLocation();
    const navigate = useNavigate();
    const { login, register } = useAuth();
    const [googleError, setGoogleError] = useState(false);
    const [mode, setMode] = useState("choose"); // "choose" | "login" | "register"
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [name, setName] = useState("");
    const [restaurantName, setRestaurantName] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (params.get("google_error")) setGoogleError(true);
    }, [location.search]);

    const submit = async (e) => {
        e.preventDefault();
        if (busy) return;
        setBusy(true);
        try {
            if (mode === "login") {
                await login(email.trim(), password);
                toast.success("Benvenuto!");
            } else {
                if (password.length < 6) {
                    toast.error("La password deve avere almeno 6 caratteri");
                    setBusy(false);
                    return;
                }
                await register({
                    email: email.trim(),
                    password,
                    name: name.trim(),
                    restaurant_name: restaurantName.trim(),
                });
                toast.success(`Benvenuto ${name.trim()}! Hai 5 giorni di prova Pro inclusi.`);
            }
            navigate("/dashboard");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen w-full bg-brand-cream bg-cream-grain flex items-center justify-center px-5 py-10">
            <div className="w-full max-w-[480px] animate-fade-up">
                <div className="flex flex-col items-center mb-8">
                    <img
                        src={LOGO_URL}
                        alt="FoodCost by Renova360"
                        className="w-full max-w-[420px] h-auto object-contain drop-shadow-sm"
                        data-testid="login-logo"
                    />
                </div>

                <div
                    className="bg-paper rounded-2xl shadow-card border border-brand-line p-7 sm:p-9"
                    data-testid="login-form"
                >
                    <h2 className="font-display text-3xl text-brand-ink">
                        {mode === "register" ? "Registra il tuo ristorante" : "Accedi al tuo account"}
                    </h2>
                    <p className="text-sm text-brand-muted mt-1.5">
                        {mode === "register"
                            ? "Crea il tuo account con email e password. 5 giorni di prova Pro inclusi."
                            : "Scegli il metodo che preferisci."}
                    </p>

                    {googleError && (
                        <div
                            className="mt-5 rounded-lg bg-red-50 border border-red-200 text-brand-danger text-sm px-4 py-2.5"
                            data-testid="login-error"
                        >
                            Accesso con Google non riuscito. Prova con email e password qui sotto.
                        </div>
                    )}

                    {mode === "choose" && (
                        <>
                            <ul className="mt-6 space-y-2.5 text-[13px] text-brand-ink/80">
                                <Bullet icon={<Sparkles size={14} />}>5 giorni di prova gratuita Pro</Bullet>
                                <Bullet icon={<ShieldCheck size={14} />}>Login federato sicuro o classico</Bullet>
                                <Bullet icon={<Lock size={14} />}>I tuoi dati restano nel tuo spazio privato</Bullet>
                            </ul>
                            <div className="mt-7">
                                <GoogleAuthButton testid="login-google" label="Continua con Google" />
                            </div>
                            <div className="my-5 flex items-center gap-3">
                                <div className="flex-1 h-px bg-brand-line" />
                                <span className="text-[11px] uppercase tracking-wider text-brand-muted">oppure</span>
                                <div className="flex-1 h-px bg-brand-line" />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <button
                                    type="button"
                                    onClick={() => setMode("login")}
                                    data-testid="login-email-toggle"
                                    className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-brand-line bg-white text-brand-ink text-sm font-semibold hover:bg-brand-cream transition"
                                >
                                    <Mail size={14} /> Accedi con email
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setMode("register")}
                                    data-testid="login-register-toggle"
                                    className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-brand-navy text-white text-sm font-semibold hover:bg-brand-navyLight transition shadow"
                                >
                                    <Sparkles size={14} /> Registra ristorante
                                </button>
                            </div>
                        </>
                    )}

                    {(mode === "login" || mode === "register") && (
                        <form onSubmit={submit} className="mt-6 space-y-3" data-testid="login-email-form">
                            {mode === "register" && (
                                <>
                                    <Field label="Nome e cognome">
                                        <input
                                            type="text"
                                            required
                                            value={name}
                                            onChange={(e) => setName(e.target.value)}
                                            data-testid="login-input-name"
                                            className="inp"
                                        />
                                    </Field>
                                    <Field label="Nome ristorante (opzionale)">
                                        <input
                                            type="text"
                                            value={restaurantName}
                                            onChange={(e) => setRestaurantName(e.target.value)}
                                            data-testid="login-input-restaurant"
                                            className="inp"
                                        />
                                    </Field>
                                </>
                            )}
                            <Field label="Email">
                                <input
                                    type="email"
                                    required
                                    autoComplete={mode === "register" ? "email" : "username"}
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    data-testid="login-input-email"
                                    className="inp"
                                />
                            </Field>
                            <Field label={mode === "register" ? "Password (min 6 caratteri)" : "Password"}>
                                <input
                                    type="password"
                                    required
                                    minLength={mode === "register" ? 6 : undefined}
                                    autoComplete={mode === "register" ? "new-password" : "current-password"}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    data-testid="login-input-password"
                                    className="inp"
                                />
                            </Field>
                            <button
                                type="submit"
                                disabled={busy}
                                data-testid="login-submit"
                                className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-brand-navy text-white text-sm font-bold hover:bg-brand-navyLight transition shadow disabled:opacity-60"
                            >
                                {busy ? <Loader2 size={14} className="animate-spin" /> : null}
                                {mode === "register" ? "Crea account" : "Accedi"}
                            </button>
                            <div className="flex justify-between text-[12px] pt-1">
                                <button
                                    type="button"
                                    onClick={() => { setMode("choose"); }}
                                    className="text-brand-muted hover:text-brand-ink transition"
                                    data-testid="login-back"
                                >
                                    ← Indietro
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setMode(mode === "register" ? "login" : "register")}
                                    className="text-brand-navy font-semibold hover:underline"
                                    data-testid="login-swap-mode"
                                >
                                    {mode === "register"
                                        ? "Hai già un account? Accedi"
                                        : "Non hai un account? Registrati"}
                                </button>
                            </div>
                        </form>
                    )}

                    <div className="mt-6 text-center text-[12px] text-brand-muted leading-relaxed">
                        Continuando accetti la nostra{" "}
                        <a
                            href="/privacy-policy"
                            className="text-brand-navy underline font-semibold"
                            data-testid="login-privacy-inline"
                        >
                            Privacy Policy
                        </a>{" "}
                        e la{" "}
                        <a
                            href="/cookie-policy"
                            className="text-brand-navy underline font-semibold"
                            data-testid="login-cookie-inline"
                        >
                            Cookie Policy
                        </a>
                        .
                    </div>
                </div>
                <LegalFooter />
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <span className="block text-[11px] uppercase tracking-wider font-bold text-brand-muted mb-1">{label}</span>
            {children}
        </label>
    );
}

function Bullet({ icon, children }) {
    return (
        <li className="flex items-start gap-2.5">
            <span className="mt-0.5 h-5 w-5 rounded-full bg-brand-gold/15 text-brand-gold flex items-center justify-center shrink-0">
                {icon}
            </span>
            <span>{children}</span>
        </li>
    );
}

function LegalFooter() {
    return (
        <div className="mt-6 flex items-center justify-center gap-4 text-[11px] uppercase tracking-[0.18em] text-brand-muted">
            <a href="/privacy-policy" className="hover:text-brand-navy transition-colors" data-testid="login-privacy-link">
                Privacy
            </a>
            <span className="text-brand-line">·</span>
            <a href="/cookie-policy" className="hover:text-brand-navy transition-colors" data-testid="login-cookie-link">
                Cookie Policy
            </a>
        </div>
    );
}
